import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import {
  convertReceiptInformationToFormValues,
  receiptInformationDefaultValues,
  TReceiptInformationForm,
  useGuestReceiptInformation,
  ReceiptInformationValidationProvider,
  ZReceiptInformationForm,
  createReceiptInformationValidation,
} from '@/features/update-receipt-information'
import { cn, ScrollProvider } from '@/shared/lib'
import { Button, Dialog, Form } from '@/shared/ui'

import { ReceiptInformation } from './receipt-information'
import { UpdateReceiptInformationButton } from './update-button'

type Props = {
  contractId: number
  onSuccess?: () => void
  onClose?: () => void
}

export const UpdateReceiptInformationForm = ({
  contractId,
  onSuccess,
  onClose,
}: Props) => {
  const t = useTranslations('receipt_information')

  const { receiptInformation } = useGuestReceiptInformation({ contractId })

  const schema = useMemo(
    () =>
      ZReceiptInformationForm.superRefine(
        createReceiptInformationValidation(t),
      ),
    [t],
  )

  const form = useForm<TReceiptInformationForm>({
    resolver: zodResolver(schema),
    defaultValues: receiptInformationDefaultValues,
  })

  const isFormEditable = receiptInformation?.isEditAvailable ?? true

  useEffect(() => {
    const convertedValues =
      convertReceiptInformationToFormValues(receiptInformation)

    if (convertedValues) {
      form.reset(convertedValues)
    }
  }, [receiptInformation, form])

  return (
    <Form.Root
      form={form}
      className={cn('flex grow flex-col overflow-auto', {
        'h-[760px]': isFormEditable,
      })}
    >
      <ReceiptInformationValidationProvider>
        <div className="scrollbar thick grow overflow-y-auto px-6">
          <ScrollProvider>
            <ReceiptInformation contractId={contractId} />
          </ScrollProvider>
        </div>
        <Dialog.Footer className="flex-row gap-2.5 p-6">
          <Dialog.Close asChild>
            <Button
              type="button"
              variant="solid"
              color="secondary"
              size="large"
              fullWidth
              onClick={onClose}
            >
              {t('button.close')}
            </Button>
          </Dialog.Close>
          {isFormEditable && (
            <UpdateReceiptInformationButton
              contractId={contractId}
              onSuccess={onSuccess}
            />
          )}
        </Dialog.Footer>
      </ReceiptInformationValidationProvider>
    </Form.Root>
  )
}
